import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { LineSegmentNode } from '../../../../../../../viewer/elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../../../viewer/elements/design/design-node/utils/point-node';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { triangulateFunctions } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/triangulate';
import { Button } from '../../../../../button/button';
import { InfoBox } from '../../../../../info-box/info-box';
import { List } from '../../../../../list/list';
import {
  ProgressDialog,
  useProgressDialogDependencies,
} from '../../../../../progress-dialog/progress-dialog';
import { Stack } from '../../../../../stack/stack';
import { useCurrentSelection } from '../hooks/use-current-selection';

type Props = {
  tool: DesignTool;
};

type SelectedGeometry = {
  lines: LineSegmentNode[][];
  polygons: LineSegmentNode[][];
  points: PointId[];
};

const warningPointsTreshold = 1000;
const showTriangulationProgressTreshold = 2000;

const Triangulate = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selection, setSelection] = React.useState<SelectedGeometry>({
    lines: [],
    polygons: [],
    points: [],
  });
  const progress = useProgressDialogDependencies(3);
  const { currentSelection, multiSelectInProgress } = useCurrentSelection(props.tool);

  const isReady =
    selection.lines.length + selection.polygons.length + selection.points.length === 0;

  const onTriangulate = React.useCallback(
    async (initialSelection: SelectedGeometry) => {
      const densifiedCurves = triangulateFunctions.densifySelectedCurves(props.tool);

      try {
        progress.controller.current.callback(
          1,
          t('section.functions.triangulate.content.save.progressBar.text', {
            ns: 'skyviewDesign',
          }) as string,
        );

        // The selection contains new line segments if any curves were replaced.
        const selection = densifiedCurves
          ? triangulateFunctions.getSelectedGeometry(props.tool, props.tool.currentSelection)
          : initialSelection;

        const indices = await triangulateFunctions.getTriangulationResult(
          props.tool,
          selection.points,
          selection.lines,
          selection.polygons,
        );

        if (progress.abortController.current.signal.aborted) {
          densifiedCurves?.revert();
          return;
        }

        await triangulateFunctions
          .applyTriangulationResult(props.tool, indices, densifiedCurves)
          .action();
      } catch (err) {
        densifiedCurves?.revert();
        throw new Error(
          t('section.functions.triangulate.content.save.errorUnknownError', {
            ns: 'skyviewDesign',
          }) as string,
        );
      }
    },
    [progress.controller, progress.abortController, props.tool, t],
  );

  const refresh = React.useCallback(() => {
    const autoTrigger = props.tool.autoTriggerTriangulation;
    props.tool.autoTriggerTriangulation = false;

    const selection = triangulateFunctions.getSelectedGeometry(props.tool, currentSelection);
    setSelection(selection);

    if (
      autoTrigger &&
      selection.lines.length + selection.polygons.length + selection.points.length > 0
    ) {
      void onTriangulate(selection);
    }
  }, [props.tool, onTriangulate, currentSelection]);

  React.useEffect(refresh, [refresh]);

  return (
    <Stack spacing={1}>
      {!isReady && selection.points.length > warningPointsTreshold && (
        <InfoBox
          color="red"
          leftIcon={{ icon: 'exclamation' }}
          title={t('section.functions.triangulate.content.warningManyPoints.title', {
            ns: 'skyviewDesign',
            count: warningPointsTreshold,
          })}
        >
          {t('section.functions.triangulate.content.warningManyPoints.text', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      )}

      {isReady ? (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
          leftTooltip={{
            title: t('section.functions.triangulate.content.infoNoPointsSelected.tooltip.title', {
              ns: 'skyviewDesign',
            }),
            content: (
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="section.functions.triangulate.content.infoNoPointsSelected.tooltip.text"
                ns="skyviewDesign"
              />
            ),
          }}
        >
          {t('section.functions.triangulate.content.infoNoPointsSelected.text', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      ) : (
        <InfoBox color="yellow">
          <Stack direction="column" spacing={0.5}>
            <span>
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="section.functions.triangulate.content.infoPointsSelected.text"
                ns="skyviewDesign"
              />
            </span>

            <List
              items={[
                t('section.functions.triangulate.content.infoPointsSelected.polygonsCount', {
                  ns: 'skyviewDesign',
                  count: selection.polygons.length,
                }),
                t('section.functions.triangulate.content.infoPointsSelected.linesCount', {
                  ns: 'skyviewDesign',
                  count: selection.lines.length,
                }),
                t('section.functions.triangulate.content.infoPointsSelected.pointsCount', {
                  ns: 'skyviewDesign',
                  count: selection.points.length,
                }),
              ]}
              marker="disc"
            />
          </Stack>
        </InfoBox>
      )}

      <Button
        color="primary"
        disabled={!multiSelectInProgress}
        variant="contained"
        onClick={async () => {
          if (selection.points.length > showTriangulationProgressTreshold) {
            progress.setShowDialog(true);
          } else {
            await onTriangulate(selection);
          }
        }}
      >
        {t('start', { ns: 'common' })}
      </Button>

      {progress.showDialog && (
        <ProgressDialog
          abortController={progress.abortController.current}
          callback={async () => {
            await onTriangulate(selection);
          }}
          progressController={progress.controller.current}
          title={t('section.functions.triangulate.content.save.progressBar.title', {
            ns: 'skyviewDesign',
          })}
          onClose={() => {
            progress.setShowDialog(false);
          }}
        />
      )}
    </Stack>
  );
};

export { Triangulate };
